/* This function at runtime only guarantees the required keys show up.
   It does not guarantee there are not extraneous keys. */
export function vectorSum<T extends { [K in keyof T]: number }>(as: T[]): T {
  const rv: any = {}; // TypeScript hack
  for (const a of as) {
    for (const key in a) {
      if (!(key in rv)) {
        rv[key] = 0;
      }
      rv[key] += a[key];
    }
  }
  return rv as T;
}

export function transpose<T>(vs: T[][]): T[][] {
  if (vs.length === 0) {
    return vs;
  }

  const dim1 = vs.length;
  const dim2 = vs[0].length;

  const rv: T[][] = [];
  for (let i = 0; i < dim2; ++i) {
    rv.push(Array(dim1).fill(null));
  }

  for (let i = 0; i < dim2; ++i) {
    for (let j = 0; j < dim1; ++j) {
      rv[i][j] = vs[j][i];
    }
  }

  return rv;
}

export interface SafeDivideOptions {
  zeroOverZero?: number;
  infOverInf?: number;
}

export interface SafeSubtractOptions {
  infMinusInf?: number;
}

export function safeDivide(
  num: number,
  den: number,
  { zeroOverZero = 1, infOverInf = 1 }: SafeDivideOptions = {},
): number {
  const quotient = num / den;
  if (Number.isNaN(quotient)) {
    if (num === 0 && den === 0) {
      return zeroOverZero;
    }
    if (!Number.isFinite(num) && !Number.isFinite(den)) {
      if ((num > 0 && den > 0) || (num < 0 && den < 0)) {
        return infOverInf;
      } else {
        return -1 * infOverInf;
      }
    }
    return 0;
  }
  return quotient;
}

export function safeSubtract(
  lhs: number,
  rhs: number,
  { infMinusInf = 0 }: SafeSubtractOptions = {},
) {
  const difference = lhs - rhs;
  if (Number.isNaN(difference)) {
    if (!Number.isFinite(lhs) && !Number.isFinite(rhs)) {
      if (lhs < rhs) {
        return -1 * infMinusInf;
      } else {
        return infMinusInf;
      }
    }
    return 0;
  }
  return difference;
}
